import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  CheckCircle,
  Clock,
  BarChart3,
  Ban,
  ClipboardCheck,
  FileText,
  User,
  AlertTriangle,
  ListChecks,
} from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  formatAssessmentSubmitSavedOnDate,
  formatAssessmentSubmitSuccessDate,
  formatReassessBeforeLabel,
} from "./assessmentSubmitSuccessCopy";
import AssessmentSubmitSuccessModal, {
  type AssessmentSubmitSuccessInfo,
} from "./AssessmentSubmitSuccessModal";
import DisconnectResolutionCard from "./DisconnectResolutionCard";
import DisconnectResolutionDetailModal from "./DisconnectResolutionDetailModal";
import {
  canEditDisconnectResolution,
  canRecordDisconnectResolution,
  getLoggedInExecutiveId,
} from "./disconnectResolutionUtils";
import { isFormalEslResponsesLocked } from "./formalEslLockUtils";
import PendingFormalAssessmentsModal, {
  type PendingFormalAssessmentRow,
} from "./PendingFormalAssessmentsModal";
import PortfolioAccountsStatusPanel from "./PortfolioAccountsStatusPanel";
import RecordResolutionModal from "./RecordResolutionModal";
import ReviewAssessmentCard from "./ReviewAssessmentCard";
import DropdownSwitcher from "../../Components/DropdownSwitcher";
import PageHeader from "../../Components/PageHeader";
import SearchInput from "../../Components/Search/SearchInput";
import TabsComponent from "../../Components/Tab";
import { StatusAccountFullReport } from "../../Container/AssessmentsContainer";
import AssessmentCalendarStatusBadge from "../../Container/AssessmentsContainer/AssessmentCalendarStatusBadge";
import PortfolioAssessChoiceModal from "../../Container/AssessmentsContainer/PortfolioAssessChoiceModal";
import ReviewAssessmentModal, {
  TemplateData,
  AssessmentResponseMap,
} from "../../Container/AssessmentsContainer/ReviewAssessmentModal";
import LevelMixContainer from "../../Container/LevelMixContainer";
import useSpeechToText from "../../Hooks/useSpeechToText";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchOrganizationLevels } from "../../Store/Reducers/AdminSlice";
import {
  fetchAssessmentTemplates,
  fetchReviewAssessments,
  fetchSelfAssessments,
  fetchDisconnectResolutions,
  submitReviewAssessment,
  clearSubmitReviewAssessmentSuccess,
  DisconnectResolution,
  ReviewAssessment,
} from "../../Store/Reducers/AssessmentSlice";
import {
  fetchPortfolioAccounts,
  type PortfolioAccount,
} from "../../Store/Reducers/CustomerWorkspaceSlice";
import { normalizeDisconnectStatus } from "../../Utils/Constants/disconnectStatusLabels";
import { isRole, ROLES } from "../../Utils/Constants/RoleConstants";
import {
  formatAssessmentDateTimePartsUtc,
  isUtcDateOverdue,
} from "../../Utils/Helper/assessmentDateUtils";
import {
  findPendingTeamPeerBanner,
  findPriorTeamPeer,
  isMyAssessmentsSelfOrigin,
  isSelfAssessmentType,
  isTeamCompletedLockedFromReassess,
  matchesAssessmentKindTab,
  matchesDisconnectKindTab,
  normalizeAssessmentKindTab,
  normalizeLevelMixScope,
  normalizeTeamAssessmentDisplayName,
  TEAM_ASSESSMENT_DESCRIPTION,
  type AssessmentKindTab,
  type LevelMixScope,
  type TeamPeerBanner,
} from "../../Utils/Helper/assessmentOriginHelpers";
import { classifyCadenceExecutive } from "../../Utils/Helper/cadenceExecutiveDisplay";
import { getLevelDisplayName } from "../../Utils/Helper/levelUtils";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";
import { toast } from "../../Utils/Helper/toast";
import {
  resolveSelfAssessmentTemplate,
  usePortfolioSelfAssessment,
} from "../MyPortfolioAccounts";

const isOverdue = (dueDate?: string | null): boolean =>
  isUtcDateOverdue(dueDate);

const STATUS_MAP = {
  pending: { badge: "warning" as const, label: "Pending" },
  in_progress: { badge: "info" as const, label: "In Progress" },
  draft: { badge: "info" as const, label: "Draft" },
  aborted: { badge: "danger" as const, label: "Aborted" },
  completed: { badge: "success-green" as const, label: "Completed" },
  published: { badge: "success-green" as const, label: "Published" },
};

const getActionLabel = (tab: string, _kind: AssessmentKindTab = "formal") => {
  if (tab === "aborted") return "View";
  if (tab === "completed") return "View";
  if (tab === "published") return "View Response";
  if (tab === "draft") return "Continue";
  return "Start Now";
};

const hasSavedResponse = (
  response: Record<string, string | string[]> | null | undefined
): boolean =>
  !!response &&
  typeof response === "object" &&
  Object.keys(response).length > 0;

const isDraftSelfAssessment = (executiveAssignment?: {
  status?: string;
  response?: Record<string, string | string[]> | null;
}): boolean =>
  executiveAssignment?.status === "in_progress" &&
  hasSavedResponse(executiveAssignment.response);

const parseKindFromUrl = (params: URLSearchParams): AssessmentKindTab => {
  const kind = params.get("kind");
  if (!kind) return "formal";
  // Legacy URLs used kind=self; normalize to team.
  if (kind === "self" || kind === "team" || kind === "formal") {
    return kind === "formal" ? "formal" : "team";
  }
  return "formal";
};

type DisconnectStatusTab = "pending" | "completed" | "published";

const DISCONNECT_STATUS_API: Record<
  DisconnectStatusTab,
  "pending_resolution" | "resolved" | "published"
> = {
  pending: "pending_resolution",
  completed: "resolved",
  published: "published",
};

const parseDisconnectStatusFromUrl = (
  params: URLSearchParams
): DisconnectStatusTab => {
  const value = params.get("drStatus");
  if (value === "pending" || value === "completed" || value === "published") {
    return value;
  }
  return "pending";
};

const parseDisconnectKindFromUrl = (
  params: URLSearchParams
): AssessmentKindTab => {
  const value = params.get("drKind");
  if (value === "team" || value === "formal" || value === "self") {
    return normalizeAssessmentKindTab(value);
  }
  return "team";
};

const ReviewAssessments: React.FC = () => {
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  type ActiveTab =
    | "assigned"
    | "status"
    | "draft"
    | "completed"
    | "published"
    | "aborted"
    | "disconnect-resolution"
    | "level-matrix";
  const VALID_TABS: ActiveTab[] = [
    "assigned",
    "status",
    "draft",
    "completed",
    "published",
    "aborted",
    "disconnect-resolution",
    "level-matrix",
  ];
  const [activeTab, setActiveTab] = useState<ActiveTab>(() => {
    const fromQuery = searchParams.get("tab");
    return (VALID_TABS as string[]).includes(fromQuery || "")
      ? (fromQuery as ActiveTab)
      : "status";
  });
  const mixScopeParam = searchParams.get("mix");
  const urlMixScope: LevelMixScope = normalizeLevelMixScope(mixScopeParam);
  const highlightAccountParam = searchParams.get("account");
  const [searchQuery, setSearchQuery] = useState("");
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewForceReadOnly, setPreviewForceReadOnly] = useState(false);
  const [templateData, setTemplateData] = useState<TemplateData | null>(null);
  const [assessmentResponses, setAssessmentResponses] = useState<
    Record<string, AssessmentResponseMap>
  >({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAccountName, setSelectedAccountName] = useState<string>("");
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [previewTeamPeerBanner, setPreviewTeamPeerBanner] =
    useState<TeamPeerBanner | null>(null);
  const [previewReassessBanner, setPreviewReassessBanner] = useState<{
    submittedAt?: string | null;
    dueDate?: string | null;
  } | null>(null);
  type AssessmentGrouping =
    | "none"
    | "assessment"
    | "account"
    | "due-date"
    | "reassess";
  const [grouping, setGrouping] = useState<AssessmentGrouping>("none");
  const [assessmentKindTab, setAssessmentKindTab] = useState<AssessmentKindTab>(
    () => parseKindFromUrl(searchParams)
  );
  const [disconnectStatusTab, setDisconnectStatusTab] =
    useState<DisconnectStatusTab>(() =>
      parseDisconnectStatusFromUrl(searchParams)
    );
  const [disconnectKindTab, setDisconnectKindTab] = useState<AssessmentKindTab>(
    () => parseDisconnectKindFromUrl(searchParams)
  );
  const [detailResolution, setDetailResolution] =
    useState<DisconnectResolution | null>(null);
  const [recordResolution, setRecordResolution] =
    useState<DisconnectResolution | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [recordModalOpen, setRecordModalOpen] = useState(false);
  const [pendingFormalListOpen, setPendingFormalListOpen] = useState(false);
  const [pendingFormalRows, setPendingFormalRows] = useState<
    PendingFormalAssessmentRow[]
  >([]);
  const [pendingFormalListLoading, setPendingFormalListLoading] =
    useState(false);
  const [pendingFormalListError, setPendingFormalListError] = useState<
    string | null
  >(null);
  const [formalSubmitSuccessInfo, setFormalSubmitSuccessInfo] =
    useState<AssessmentSubmitSuccessInfo | null>(null);
  const [disconnectSubmitSuccessInfo, setDisconnectSubmitSuccessInfo] =
    useState<AssessmentSubmitSuccessInfo | null>(null);
  const notifyPastDueLocked = useCallback(() => {
    toast.info(
      "The due date has passed. You can view a submitted response, or there is no response submitted."
    );
  }, []);

  const dispatch = useAppDispatch();
  const {
    reviewAssessments,
    reviewAssessmentsLoading,
    reviewAssessmentsError,
    isSubmittingReviewAssessment,
    submitReviewAssessmentError,
    disconnectResolutions,
    isLoadingDisconnectResolutions,
    disconnectResolutionsError,
    templates: templatesPayload,
  } = useAppSelector(state => state.assessment);
  const {
    items: portfolioAccounts,
    loading: portfolioAccountsLoading,
    error: portfolioAccountsError,
  } = useAppSelector(state => state.customerWorkspace.portfolioAccounts);
  const { items: orgPortfolioAccounts, loading: orgPortfolioLoading } =
    useAppSelector(state => state.customerWorkspace.orgPortfolioAccounts);

  const navigate = useNavigate();

  const withOrg = useCallback(
    (path: string) => {
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      return orgSlug ? `/${orgSlug}${normalizedPath}` : normalizedPath;
    },
    [orgSlug]
  );

  const goToMyMix = useCallback(
    (accountId: string) => {
      navigate(
        withOrg(
          `/pending-assessments?tab=level-matrix&mix=my&account=${accountId}`
        )
      );
    },
    [navigate, withOrg]
  );

  const selfAssessmentTemplate = useMemo(
    () => resolveSelfAssessmentTemplate(templatesPayload),
    [templatesPayload]
  );

  const selfAssessment = usePortfolioSelfAssessment(
    selfAssessmentTemplate,
    withOrg
  );
  const [statusReportAccount, setStatusReportAccount] =
    useState<PortfolioAccount | null>(null);

  const loggedInUser = useMemo(
    () => getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER),
    []
  );
  const executiveId = useMemo(
    () => getLoggedInExecutiveId(loggedInUser),
    [loggedInUser]
  );
  const allowInProgressReport =
    isRole(loggedInUser?.role, ROLES.EXECUTIVE_SPONSOR_LEAD) ||
    isRole(loggedInUser?.role, ROLES.VENDOR_ADMIN) ||
    loggedInUser?.executiveOps === true;

  const loadReviewAssessmentsForTab = useCallback(
    (
      tab: typeof activeTab,
      kindTab: AssessmentKindTab = assessmentKindTab,
      disconnectTab: DisconnectStatusTab = disconnectStatusTab
    ) => {
      if (tab === "level-matrix" || tab === "status") return;

      if (tab === "disconnect-resolution") {
        dispatch(
          fetchDisconnectResolutions({
            status: DISCONNECT_STATUS_API[disconnectTab],
          })
        );
        return;
      }

      if (!executiveId) return;

      if (
        kindTab === "team" &&
        (tab === "draft" || tab === "assigned" || tab === "completed")
      ) {
        dispatch(
          fetchSelfAssessments({
            status:
              tab === "draft"
                ? "draft"
                : tab === "assigned"
                  ? "assigned"
                  : "completed",
            account_id: "",
          })
        );
        return;
      }

      if (tab === "draft") {
        dispatch(
          fetchReviewAssessments({
            executive_id: executiveId,
            tab: "draft",
            is_draft: true,
            account_id: "",
          })
        );
        return;
      }

      dispatch(
        fetchReviewAssessments({
          executive_id: executiveId,
          tab,
          account_id: "",
        })
      );
    },
    [dispatch, executiveId, assessmentKindTab, disconnectStatusTab]
  );

  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId as typeof activeTab);
      if (tabId === "completed" || tabId === "draft" || tabId === "assigned") {
        setAssessmentKindTab("formal");
      }
      if (tabId === "disconnect-resolution") {
        setDisconnectStatusTab("pending");
        setDisconnectKindTab("team");
      }
      // Reassess grouping exists only on Completed.
      setGrouping(prev =>
        tabId !== "completed" && prev === "reassess" ? "none" : prev
      );
      // Manual tab clicks clear any level-matrix deep-link params.
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set("tab", tabId);
        next.delete("mix");
        next.delete("account");
        next.delete("kind");
        next.delete("myCadence");
        next.delete("kpi");
        if (tabId === "disconnect-resolution") {
          next.set("drStatus", "pending");
          next.set("drKind", "team");
        } else {
          next.delete("drStatus");
          next.delete("drKind");
        }
        return next;
      });
    },
    [setSearchParams]
  );

  // Keep activeTab in sync when the URL changes externally (deep link, back/forward).
  useEffect(() => {
    const fromQuery = searchParams.get("tab");
    if (fromQuery && (VALID_TABS as string[]).includes(fromQuery)) {
      setActiveTab(prev =>
        prev === fromQuery ? prev : (fromQuery as ActiveTab)
      );
    }
    const kindFromQuery = searchParams.get("kind");
    if (
      kindFromQuery === "self" ||
      kindFromQuery === "team" ||
      kindFromQuery === "formal"
    ) {
      const next = kindFromQuery === "formal" ? "formal" : "team";
      setAssessmentKindTab(prev => (prev === next ? prev : next));
    }
    const drStatusFromQuery = searchParams.get("drStatus");
    if (
      drStatusFromQuery === "pending" ||
      drStatusFromQuery === "completed" ||
      drStatusFromQuery === "published"
    ) {
      setDisconnectStatusTab(prev =>
        prev === drStatusFromQuery ? prev : drStatusFromQuery
      );
    }
    const drKindFromQuery = searchParams.get("drKind");
    if (
      drKindFromQuery === "team" ||
      drKindFromQuery === "formal" ||
      drKindFromQuery === "self"
    ) {
      const nextKind = normalizeAssessmentKindTab(drKindFromQuery);
      setDisconnectKindTab(prev => (prev === nextKind ? prev : nextKind));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleAssessmentKindTabChange = useCallback(
    (tabId: string) => {
      const kind = tabId as AssessmentKindTab;
      setAssessmentKindTab(kind);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set("kind", kind);
        return next;
      });
    },
    [setSearchParams]
  );

  const handleDisconnectKindTabChange = useCallback(
    (tabId: string) => {
      const nextKind = normalizeAssessmentKindTab(tabId);
      setDisconnectKindTab(nextKind);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set("drKind", nextKind);
        return next;
      });
    },
    [setSearchParams]
  );

  const handleDisconnectStatusTabChange = useCallback(
    (tabId: string) => {
      const nextTab = tabId as DisconnectStatusTab;
      setDisconnectStatusTab(nextTab);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set("drStatus", nextTab);
        return next;
      });
    },
    [setSearchParams]
  );

  const assessmentKindTabs = useMemo(
    () => [
      {
        id: "team",
        label: "Team assessments",
        icon: <User size={18} />,
      },
      {
        id: "formal",
        label: "Formal assessments",
        icon: <FileText size={18} />,
      },
    ],
    []
  );

  const disconnectKindTabs = useMemo(
    () => [
      {
        id: "team",
        label: "Team Disconnect",
        icon: <User size={18} />,
      },
      {
        id: "formal",
        label: "Formal Disconnect",
        icon: <FileText size={18} />,
      },
    ],
    []
  );

  const disconnectStatusTabs = useMemo(
    () => [
      { id: "pending", label: "Pending", icon: <Clock size={14} /> },
      {
        id: "completed",
        label: "Completed",
        icon: <CheckCircle size={14} />,
      },
      {
        id: "published",
        label: "Published",
        icon: <FileText size={14} />,
      },
    ],
    []
  );

  const tabs = useMemo(
    () => [
      {
        id: "status",
        label: (
          <span className="inline-flex items-center gap-2.5">
            <ListChecks className="h-[18px] w-[18px]" size={18} />
            Status
          </span>
        ),
      },
      {
        id: "assigned",
        label: (
          <span className="inline-flex items-center gap-2.5">
            <Clock className="h-[18px] w-[18px]" size={18} />
            Assigned
          </span>
        ),
      },
      {
        id: "draft",
        label: (
          <span className="inline-flex items-center gap-2.5">
            <Clock className="h-[18px] w-[18px]" size={18} />
            Draft
          </span>
        ),
      },
      {
        id: "completed",
        label: (
          <span className="inline-flex items-center gap-2.5">
            <CheckCircle className="h-[18px] w-[18px]" size={18} />
            Completed
          </span>
        ),
      },
      {
        id: "aborted",
        label: (
          <span className="inline-flex items-center gap-2.5">
            <Ban className="h-[18px] w-[18px]" size={18} />
            Aborted
          </span>
        ),
      },
      {
        id: "disconnect-resolution",
        label: (
          <span className="inline-flex items-center gap-2.5">
            <AlertTriangle className="h-[18px] w-[18px]" size={18} />
            Disconnect Resolution
          </span>
        ),
      },
      {
        id: "level-matrix",
        label: (
          <span className="inline-flex items-center gap-2.5">
            <BarChart3 className="h-[18px] w-[18px]" size={18} />
            Levels Mix
          </span>
        ),
      },
    ],
    []
  );

  const groupingOptions = useMemo(() => {
    const options: { label: string; value: AssessmentGrouping }[] = [
      { label: "No Grouping", value: "none" },
      { label: "Group by Assessment", value: "assessment" },
      { label: "Group by Account", value: "account" },
      { label: "Group by Due Date", value: "due-date" },
    ];
    if (activeTab === "completed") {
      options.push({ label: "Group by Reassess", value: "reassess" });
    }
    return options;
  }, [activeTab]);

  const refetchAssessments = useCallback(() => {
    loadReviewAssessmentsForTab(
      activeTab,
      assessmentKindTab,
      disconnectStatusTab
    );
    if (activeTab === "status") {
      dispatch(fetchPortfolioAccounts("mine"));
      dispatch(fetchPortfolioAccounts("org"));
    }
  }, [
    loadReviewAssessmentsForTab,
    activeTab,
    assessmentKindTab,
    disconnectStatusTab,
    dispatch,
  ]);

  useEffect(() => {
    refetchAssessments();
  }, [refetchAssessments]);

  useEffect(() => {
    dispatch(fetchAssessmentTemplates({}));
  }, [dispatch]);

  useEffect(() => {
    if (activeTab !== "status") return;
    dispatch(fetchOrganizationLevels());
  }, [dispatch, activeTab]);

  const handleAssessPortfolioAccount = async (account: PortfolioAccount) => {
    const result = await selfAssessment.openForAccount(
      {
        id: account.account_id,
        name: account.account_name,
      },
      { freshStart: true }
    );
    if (result === "finalization" || result === "esl_locked") {
      notifyPastDueLocked();
    }
  };

  const openFormalAssessmentTaking = useCallback(
    (assessment: ReviewAssessment, accountId: string, accountName: string) => {
      if (isFormalEslResponsesLocked(assessment, accountId)) {
        notifyPastDueLocked();
        return;
      }

      const account = assessment.accounts.find(
        acc => acc.account_id === accountId
      );
      const executiveAssignment = account?.executives.find(
        exec => exec.executive_id === executiveId
      );

      const templateDataToPass: TemplateData = {
        id: assessment.assessment_id,
        name: normalizeTeamAssessmentDisplayName(
          assessment.assessment_name,
          assessment.assessment_name || "Untitled Assessment"
        ),
        json_schema: assessment.questions,
        status: (assessment.status || "").toLowerCase(),
      };

      setAssessmentResponses(prev => {
        if (executiveAssignment?.response) {
          return {
            ...prev,
            [assessment.assessment_id]: executiveAssignment.response as Record<
              string,
              string | string[]
            >,
          };
        }
        const updated = { ...prev };
        delete updated[assessment.assessment_id];
        return updated;
      });

      setTemplateData(templateDataToPass);
      setSelectedAccountName(accountName || account?.account_name || "");
      setSelectedAccountId(accountId);
      setCurrentQuestionIndex(0);
      setPreviewForceReadOnly(false);
      setPreviewReassessBanner(null);
      setPreviewModalOpen(true);
    },
    [executiveId, notifyPastDueLocked]
  );

  const loadAssignedFormalForAccount = useCallback(
    async (accountId: string): Promise<ReviewAssessment[]> => {
      if (!executiveId) return [];
      const response = await dispatch(
        fetchReviewAssessments({
          executive_id: executiveId,
          tab: "assigned",
          account_id: accountId,
        })
      ).unwrap();
      return (response.assessments || []).filter(
        assessment => !isSelfAssessmentType(assessment.assessment_type)
      );
    },
    [dispatch, executiveId]
  );

  const handleTakeFormalAssessment = useCallback(async () => {
    const accountId = selfAssessment.accountId;
    const accountName = selfAssessment.accountName;
    const pendingCount = selfAssessment.pendingFormalCount;
    const validationIds = selfAssessment.pendingFormalAssessments
      .map(item => item.assessment_id)
      .filter(Boolean) as string[];
    const resetSelfModal = selfAssessment.resetModal;

    if (!accountId || pendingCount <= 0) return;

    setPendingFormalListLoading(true);
    setPendingFormalListError(null);

    try {
      const formalAssessments = await loadAssignedFormalForAccount(accountId);
      const matched =
        validationIds.length > 0
          ? formalAssessments.filter(a =>
              validationIds.includes(a.assessment_id)
            )
          : formalAssessments;

      const rows: PendingFormalAssessmentRow[] = (
        matched.length > 0 ? matched : formalAssessments
      ).map(assessment => ({
        assessment,
        accountId,
        accountName,
      }));

      if (rows.length === 0) {
        toast.error("Unable to load pending formal assessments.");
        return;
      }

      resetSelfModal();

      if (pendingCount === 1) {
        const row = rows[0];
        openFormalAssessmentTaking(
          row.assessment,
          row.accountId,
          row.accountName
        );
        return;
      }

      setPendingFormalRows(rows);
      setPendingFormalListOpen(true);
    } catch (error: any) {
      toast.error(
        error?.message || "Unable to load pending formal assessments."
      );
      setPendingFormalListError(
        error?.message || "Unable to load pending formal assessments."
      );
    } finally {
      setPendingFormalListLoading(false);
    }
  }, [
    selfAssessment.accountId,
    selfAssessment.accountName,
    selfAssessment.pendingFormalCount,
    selfAssessment.pendingFormalAssessments,
    selfAssessment.resetModal,
    loadAssignedFormalForAccount,
    openFormalAssessmentTaking,
  ]);

  const handlePendingFormalListAssess = useCallback(
    (row: PendingFormalAssessmentRow) => {
      setPendingFormalListOpen(false);
      setPendingFormalRows([]);
      selfAssessment.resetModal();
      openFormalAssessmentTaking(
        row.assessment,
        row.accountId,
        row.accountName
      );
    },
    [openFormalAssessmentTaking, selfAssessment.resetModal]
  );

  const handleClosePendingFormalList = useCallback(() => {
    setPendingFormalListOpen(false);
    setPendingFormalRows([]);
    setPendingFormalListError(null);
    setPendingFormalListLoading(false);
  }, []);

  const handleSelfAssessmentGoToMyAssessments = () => {
    void selfAssessment.goToMyAssessments();
  };

  const handleSelfAssessmentSubmit = async () => {
    const success = await selfAssessment.submit();
    if (success) {
      refetchAssessments();
    }
  };

  const handleSelfAssessmentSaveDraft = async () => {
    const success = await selfAssessment.saveDraft();
    if (success) {
      refetchAssessments();
    }
  };

  // Map API response to AssessmentCardItem format
  const allAssessmentCards = useMemo(() => {
    if (!reviewAssessments || reviewAssessments.length === 0) {
      return [];
    }

    const cards: any = [];

    reviewAssessments.forEach(assessment => {
      assessment.accounts.forEach(account => {
        const executiveAssignment = account.executives.find(
          exec => exec.executive_id === executiveId
        );

        const isDraft = isDraftSelfAssessment(executiveAssignment);
        if (activeTab === "assigned" && isDraft) return;

        const disconnectStatus = normalizeDisconnectStatus(
          executiveAssignment?.disconnect_status
        );
        const disconnectLocksEdit =
          disconnectStatus === "pending_resolution" ||
          disconnectStatus === "resolved";

        const publishedDateRaw =
          (executiveAssignment as any)?.published_on ??
          (assessment as any)?.published_at ??
          null;

        const completedOnRaw =
          (executiveAssignment as any)?.completed_on ?? null;

        const publishedDate = publishedDateRaw
          ? new Date(publishedDateRaw)
          : null;
        const completedDate = completedOnRaw ? new Date(completedOnRaw) : null;

        // When viewing the "published" or "draft" tab, force the UI status/badge.
        const status =
          activeTab === "published"
            ? ("published" as const)
            : activeTab === "draft"
              ? ("draft" as const)
              : executiveAssignment?.status || "completed";
        const statusInfo =
          STATUS_MAP[status as keyof typeof STATUS_MAP] || STATUS_MAP.completed;

        const dueDate = assessment.due_date
          ? new Date(assessment.due_date)
          : null;

        const completedOnLabel = completedOnRaw
          ? formatAssessmentDateTimePartsUtc(completedOnRaw, "DMY_SHORT").date
          : undefined;

        const publishedOnLabel = publishedDateRaw
          ? formatAssessmentDateTimePartsUtc(publishedDateRaw, "DMY_SHORT").date
          : undefined;

        const hasSubmittedResponse = hasSavedResponse(
          executiveAssignment?.response
        );
        const isCompletedWithoutResponse =
          (activeTab === "completed" || activeTab === "aborted") &&
          !hasSubmittedResponse;

        const createdAtMs = assessment.created_at
          ? new Date(assessment.created_at).getTime()
          : 0;
        const abortAtMs = (assessment as any)?.abort_at
          ? new Date((assessment as any).abort_at).getTime()
          : 0;

        const card = {
          id: `${assessment.assessment_id}-${account.account_id}`,
          assessmentType: assessment.assessment_type,
          notes: assessment.notes,
          abortReason: assessment?.abort_reason,
          abortAt: (assessment as any)?.abort_at
            ? formatAssessmentDateTimePartsUtc(
                (assessment as any).abort_at,
                "DMY_SHORT"
              ).date
            : undefined,
          assessmentId: assessment.assessment_id,
          assessmentName: normalizeTeamAssessmentDisplayName(
            assessment.assessment_name,
            assessment.assessment_name || "Untitled Assessment",
            account.account_name
          ),
          description: (() => {
            const isTeam = isMyAssessmentsSelfOrigin(assessment, activeTab);
            const normalized = normalizeTeamAssessmentDisplayName(
              assessment.description,
              isTeam ? TEAM_ASSESSMENT_DESCRIPTION : ""
            );
            return normalized || undefined;
          })(),
          questionnaireLabel: assessment.category,
          dueDateLabel: assessment.due_date
            ? formatAssessmentDateTimePartsUtc(assessment.due_date, "DMY_SHORT")
                .date
            : undefined,
          dueDateValue: dueDate?.getTime() ?? 0,
          completedOnLabel:
            activeTab === "published"
              ? (publishedOnLabel ?? completedOnLabel)
              : completedOnLabel,
          completedOnValue:
            activeTab === "published"
              ? (publishedDate?.getTime() ?? completedDate?.getTime())
              : completedDate?.getTime(),
          sortDateValue:
            activeTab === "assigned" || activeTab === "draft"
              ? createdAtMs || dueDate?.getTime() || 0
              : activeTab === "aborted"
                ? abortAtMs ||
                  completedDate?.getTime() ||
                  publishedDate?.getTime() ||
                  createdAtMs ||
                  dueDate?.getTime() ||
                  0
                : (publishedDate?.getTime() ??
                  completedDate?.getTime() ??
                  createdAtMs ??
                  dueDate?.getTime() ??
                  0),
          assignedOnLabel: formatAssessmentDateTimePartsUtc(
            assessment.created_at,
            "DMY_SHORT"
          ).date,
          assignedByLabel: assessment.created_by,
          accountName: account.account_name,
          cadenceTag:
            activeTab === "assigned" ||
            activeTab === "aborted" ||
            activeTab === "completed"
              ? classifyCadenceExecutive({
                  notes: (executiveAssignment as any)?.notes,
                  submitted_at:
                    (executiveAssignment as any)?.submitted_at ||
                    (executiveAssignment as any)?.completed_on ||
                    null,
                  is_responded: hasSubmittedResponse,
                  carried: Boolean(
                    (executiveAssignment as any)?.notes?.carry_forward ||
                      (executiveAssignment as any)?.notes?.carried_in_at_publish
                  ),
                }).tag
              : null,
          accountIdentifier: account.account_id,
          accountTier: account.account_tier,
          isHeld:
            activeTab === "assigned" &&
            Boolean((executiveAssignment as any)?.held),
          statusLabel: isCompletedWithoutResponse
            ? "No response"
            : activeTab === "assigned" && (executiveAssignment as any)?.held
              ? "Held · counts after the disconnect"
              : statusInfo.label,
          statusBadge: isCompletedWithoutResponse
            ? ("warning" as const)
            : statusInfo.badge,
          actionLabel: isCompletedWithoutResponse
            ? null
            : getActionLabel(activeTab, assessmentKindTab),
          showEditButton:
            !isCompletedWithoutResponse &&
            activeTab === "completed" &&
            ((assessmentKindTab === "formal" &&
              (executiveAssignment?.publishing || "").toLowerCase() !==
                "published" &&
              !disconnectLocksEdit) ||
              (assessmentKindTab === "team" &&
                !isOverdue(assessment.due_date) &&
                !disconnectLocksEdit &&
                !isTeamCompletedLockedFromReassess(
                  assessment,
                  executiveAssignment?.publishing
                ) &&
                // Legacy superseded Teams only — Formal Hijack keeps Team editable.
                !assessment.superseded_by_formal)),
          hasSubmittedResponse,
          contextTab: activeTab,
          isOverdue:
            activeTab === "assigned" &&
            Boolean(assessment.due_date) &&
            isOverdue(assessment.due_date),
        };

        cards.push(card);
      });
    });

    // Latest assessments first on every tab
    cards.sort(
      (a: any, b: any) => (b.sortDateValue || 0) - (a.sortDateValue || 0)
    );

    // Filter by search query (local filtering by assessment name)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      return cards.filter((card: any) =>
        card.assessmentName.toLowerCase().includes(query)
      );
    }

    return cards;
  }, [
    reviewAssessments,
    executiveId,
    activeTab,
    searchQuery,
    assessmentKindTab,
  ]);

  const assessmentCards = useMemo(() => {
    if (
      activeTab === "level-matrix" ||
      activeTab === "disconnect-resolution" ||
      activeTab === "status"
    ) {
      return [];
    }
    if (
      activeTab !== "completed" &&
      activeTab !== "draft" &&
      activeTab !== "assigned"
    ) {
      return allAssessmentCards;
    }

    return allAssessmentCards.filter(
      (card: {
        assessmentType?: string | null;
        notes?: ReviewAssessment["notes"];
      }) =>
        matchesAssessmentKindTab(
          { assessment_type: card.assessmentType, notes: card.notes },
          assessmentKindTab,
          activeTab
        )
    );
  }, [allAssessmentCards, assessmentKindTab, activeTab]);

  const filteredDisconnectResolutions = useMemo(() => {
    if (!disconnectResolutions?.length) return [];
    const expectedStatus = DISCONNECT_STATUS_API[disconnectStatusTab];
    let items = disconnectResolutions.filter(resolution =>
      matchesDisconnectKindTab(
        resolution.assessmentType,
        disconnectKindTab,
        null,
        resolution.assessmentName
      )
    );
    items = items.filter(resolution => {
      const status = (resolution.status || "").toLowerCase();
      if (expectedStatus === "published") {
        return status === "published" || status === "unresolved";
      }
      return status === expectedStatus;
    });
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      items = items.filter(
        resolution =>
          (resolution.assessmentName || "").toLowerCase().includes(query) ||
          (resolution.accountName || "").toLowerCase().includes(query)
      );
    }
    return items.sort((a, b) => {
      const aTime = a.triggered_at ? new Date(a.triggered_at).getTime() : 0;
      const bTime = b.triggered_at ? new Date(b.triggered_at).getTime() : 0;
      return bTime - aTime;
    });
  }, [
    disconnectResolutions,
    searchQuery,
    disconnectStatusTab,
    disconnectKindTab,
  ]);

  const disconnectEmptyMessage = useMemo(() => {
    if (searchQuery.trim()) return "Try adjusting your search criteria";
    const kindLabel = disconnectKindTab === "team" ? "team" : "formal";
    if (disconnectStatusTab === "pending") {
      return `No ${kindLabel} disconnect resolutions awaiting primary sponsor`;
    }
    if (disconnectStatusTab === "completed") {
      return `No ${kindLabel} disconnect resolutions pending until due`;
    }
    return `No published ${kindLabel} disconnect resolutions`;
  }, [disconnectStatusTab, disconnectKindTab, searchQuery]);

  const canRecordResolution = useCallback(
    (resolution: DisconnectResolution) =>
      canRecordDisconnectResolution(resolution, executiveId),
    [executiveId]
  );

  const canEditResolution = useCallback(
    (resolution: DisconnectResolution) =>
      canEditDisconnectResolution(resolution, executiveId),
    [executiveId]
  );

  const handleOpenDetailModal = useCallback(
    (resolution: DisconnectResolution) => {
      setDetailResolution(resolution);
      setDetailModalOpen(true);
    },
    []
  );

  const handleOpenRecordModal = useCallback(
    (resolution: DisconnectResolution) => {
      setRecordResolution(resolution);
      setRecordModalOpen(true);
      setDetailModalOpen(false);
    },
    []
  );

  const handleCloseDetailModal = useCallback(() => {
    setDetailModalOpen(false);
    setDetailResolution(null);
  }, []);

  const handleCloseRecordModal = useCallback(() => {
    setRecordModalOpen(false);
    setRecordResolution(null);
  }, []);

  const handleRecordResolutionSuccess = useCallback(
    (resolution: DisconnectResolution) => {
      loadReviewAssessmentsForTab(
        "disconnect-resolution",
        assessmentKindTab,
        disconnectStatusTab
      );
      setDisconnectSubmitSuccessInfo({
        kind: "disconnect",
        accountId: resolution.account_id,
        accountName: resolution.accountName || "the account",
        isSelfDisconnect: isSelfAssessmentType(resolution.assessmentType),
        dueDateLabel: formatAssessmentSubmitSuccessDate(resolution.due_date),
      });
    },
    [loadReviewAssessmentsForTab, assessmentKindTab, disconnectStatusTab]
  );

  // Speech-to-text hook setup
  const { speechSupported, isListening, startListening, stopListening } =
    useSpeechToText();

  const [voiceModeEnabled, setVoiceModeEnabled] = useState(false);

  const handleToggleVoiceMode = useCallback(
    (next: boolean) => {
      if (!speechSupported && next) {
        setVoiceModeEnabled(false);
        return;
      }

      setVoiceModeEnabled(next);
      if (next) {
        startListening();
      } else {
        stopListening();
      }
    },
    [speechSupported, startListening, stopListening]
  );

  const handleCloseReviewModal = useCallback(() => {
    setPreviewModalOpen(false);
    setPreviewForceReadOnly(false);
    setTemplateData(null);
    setPreviewTeamPeerBanner(null);
    setPreviewReassessBanner(null);
    setCurrentQuestionIndex(0);
    setSelectedAccountId("");
    setVoiceModeEnabled(false);
    stopListening();
    dispatch(clearSubmitReviewAssessmentSuccess());
  }, [stopListening, dispatch]);

  const handleReviewResponseChange = useCallback(
    (questionId: string, value: string | string[]) => {
      setAssessmentResponses(prev => {
        const activeAssessmentId = templateData?.id;
        if (!activeAssessmentId) return prev;

        return {
          ...prev,
          [activeAssessmentId]: {
            ...(prev[activeAssessmentId] || {}),
            [questionId]: value,
          },
        };
      });
    },
    [templateData]
  );

  const submitAssessment = useCallback(
    async (isDraft: boolean) => {
      if (!templateData || !selectedAccountId) return;

      const rawResponses = assessmentResponses[templateData.id] || {};
      const responses = Object.fromEntries(
        Object.entries(rawResponses).filter(([, value]) => value !== undefined)
      ) as Record<string, string | string[]>;

      try {
        const submitResult = await dispatch(
          submitReviewAssessment({
            assessment_id: templateData.id,
            account_id: selectedAccountId,
            responses,
            is_draft: isDraft,
          })
        ).unwrap();

        if (isDraft) {
          toast.success("Draft saved successfully");
        } else {
          const levelLabel = getLevelDisplayName(
            (responses.maturity_level as string) ?? ""
          );
          const sourceAssessment = reviewAssessments?.find(
            a => a.assessment_id === templateData.id
          );
          const dueRaw =
            previewReassessBanner?.dueDate ||
            sourceAssessment?.due_date ||
            null;
          setFormalSubmitSuccessInfo({
            kind: "formal",
            accountId: selectedAccountId,
            accountName: selectedAccountName || "the account",
            levelLabel: responses.maturity_level ? levelLabel : null,
            dateLabel: formatAssessmentSubmitSavedOnDate(),
            dueDateLabel: formatAssessmentSubmitSuccessDate(dueRaw),
            remainderCountsOnLabel: formatAssessmentSubmitSuccessDate(
              submitResult?.remainder_counts_on
            ),
            inputDisposition: submitResult?.input_disposition ?? null,
            reassessBeforeLabel: formatReassessBeforeLabel(
              submitResult?.round_due_date
            ),
          });
        }
        refetchAssessments();

        if (!isDraft) {
          handleCloseReviewModal();
        }
      } catch (error: any) {
        const defaultMsg = isDraft
          ? "Failed to save draft"
          : "Failed to submit assessment";
        const errorMessage = error || submitReviewAssessmentError || defaultMsg;
        toast.error(errorMessage);
        console.error(defaultMsg, error);
      }
    },
    [
      dispatch,
      templateData,
      selectedAccountId,
      selectedAccountName,
      assessmentResponses,
      refetchAssessments,
      handleCloseReviewModal,
      submitReviewAssessmentError,
      reviewAssessments,
      previewReassessBanner?.dueDate,
    ]
  );

  const handleSaveDraft = useCallback(
    () => submitAssessment(true),
    [submitAssessment]
  );
  const handleSubmitAssessment = useCallback(
    () => submitAssessment(false),
    [submitAssessment]
  );

  const handleOpenPreviewAssessment = useCallback(
    async (card: any, options?: { mode?: "view" | "edit" }) => {
      const assessment = reviewAssessments?.find(
        a => a.assessment_id === card.assessmentId
      );
      if (!assessment) return;

      const account = assessment.accounts.find(
        acc => acc.account_id === card.accountIdentifier
      );
      if (!account) return;

      const executiveAssignment = account.executives.find(
        exec => exec.executive_id === executiveId
      );
      const actionMode = options?.mode ?? "view";
      const disconnectStatus = normalizeDisconnectStatus(
        executiveAssignment?.disconnect_status
      );
      const disconnectLocksEdit =
        disconnectStatus === "pending_resolution" ||
        disconnectStatus === "resolved";

      if (
        (activeTab === "completed" &&
          isMyAssessmentsSelfOrigin(assessment, activeTab) &&
          !hasSavedResponse(executiveAssignment?.response)) ||
        (activeTab === "aborted" &&
          !hasSavedResponse(executiveAssignment?.response))
      ) {
        return;
      }

      const savedResponse = executiveAssignment?.response;
      const assessmentStatus = (assessment.status || "").toUpperCase();
      // Leftover PRE_PUBLISH: skip draft prefill (due date passed / responses locked).
      const skipPrefillForEslBuffer = assessmentStatus === "PRE_PUBLISH";
      const initialResponses =
        !skipPrefillForEslBuffer &&
        savedResponse &&
        typeof savedResponse === "object" &&
        Object.keys(savedResponse).length > 0
          ? (savedResponse as AssessmentResponseMap)
          : undefined;

      // Self Assigned / Draft / Completed Re-assess → portfolio Self flow with prefill
      if (
        (isSelfAssessmentType(assessment.assessment_type) ||
          assessmentKindTab === "team") &&
        (activeTab === "assigned" ||
          activeTab === "draft" ||
          (activeTab === "completed" &&
            assessmentKindTab === "team" &&
            actionMode === "edit" &&
            !isOverdue(assessment.due_date) &&
            !disconnectLocksEdit &&
            !isTeamCompletedLockedFromReassess(
              assessment,
              executiveAssignment?.publishing
            )))
      ) {
        const priorPeer =
          (activeTab === "assigned" || activeTab === "draft") &&
          assessmentKindTab === "team"
            ? findPendingTeamPeerBanner(account.executives, executiveId)
            : null;

        const openResult = await selfAssessment.openForAccount(
          {
            id: card.accountIdentifier,
            name: card.accountName,
          },
          {
            fallbackAssessmentId: card.assessmentId,
            initialResponses,
            teamPeerBanner: priorPeer,
            reassessBanner:
              activeTab === "completed"
                ? {
                    submittedAt:
                      executiveAssignment?.updated_at ||
                      executiveAssignment?.completed_on ||
                      null,
                    dueDate: assessment.due_date || null,
                  }
                : null,
          }
        );
        if (openResult === "finalization" || openResult === "esl_locked") {
          notifyPastDueLocked();
        }
        return;
      }

      // Formal Assigned → portfolio Formal flow (CF/RA when cadence is Current)
      if (
        activeTab === "assigned" &&
        assessmentKindTab === "formal" &&
        !isSelfAssessmentType(assessment.assessment_type)
      ) {
        if (disconnectLocksEdit) {
          notifyPastDueLocked();
          return;
        }
        if (
          isFormalEslResponsesLocked(
            assessment,
            account.account_id || card.accountIdentifier
          )
        ) {
          notifyPastDueLocked();
          return;
        }

        const openResult = await selfAssessment.openForAccount(
          {
            id: card.accountIdentifier,
            name: card.accountName,
          },
          {
            fallbackAssessmentId: card.assessmentId,
          }
        );
        if (openResult === "finalization" || openResult === "esl_locked") {
          notifyPastDueLocked();
        }
        return;
      }

      const isTeamCompletedView =
        (activeTab === "completed" || activeTab === "published") &&
        assessmentKindTab === "team" &&
        isMyAssessmentsSelfOrigin(assessment, activeTab);
      const completedPeer = isTeamCompletedView
        ? findPriorTeamPeer(account.executives, executiveId)
        : null;
      setPreviewTeamPeerBanner(
        completedPeer ? { ...completedPeer, variant: "completed" } : null
      );

      const statusUpper = (assessment.status || "").toUpperCase();
      const accountPublishing = (
        executiveAssignment?.publishing || ""
      ).toLowerCase();
      const isPublishedView =
        activeTab === "published" ||
        statusUpper === "PUBLISHED" ||
        accountPublishing === "published";
      const canEditFormalCompleted =
        activeTab === "completed" &&
        assessmentKindTab === "formal" &&
        !isPublishedView &&
        !disconnectLocksEdit;
      const canEditSelfCompleted =
        activeTab === "completed" &&
        assessmentKindTab === "team" &&
        !isOverdue(assessment.due_date) &&
        !disconnectLocksEdit &&
        !isTeamCompletedLockedFromReassess(
          assessment,
          executiveAssignment?.publishing
        );
      const canEditCompleted = canEditFormalCompleted || canEditSelfCompleted;

      if (
        activeTab === "completed" ||
        activeTab === "published" ||
        activeTab === "aborted"
      ) {
        // View Response is always read-only; Re-assess unlocks until hub publish
        // (Formal / Team Self→AL) or until due (open Team cycle). Disconnect locks edits.
        setPreviewForceReadOnly(
          activeTab === "aborted" ||
            !(actionMode === "edit" && canEditCompleted)
        );
      } else {
        setPreviewForceReadOnly(isPublishedView);
      }

      if (actionMode === "edit" && canEditCompleted) {
        setPreviewReassessBanner({
          submittedAt:
            executiveAssignment?.updated_at ||
            executiveAssignment?.completed_on ||
            null,
          dueDate: assessment.due_date || null,
        });
      } else {
        setPreviewReassessBanner(null);
      }

      if (
        activeTab === "assigned" &&
        !isSelfAssessmentType(assessment.assessment_type)
      ) {
        if (disconnectLocksEdit) {
          notifyPastDueLocked();
          return;
        }
        if (
          isFormalEslResponsesLocked(
            assessment,
            account.account_id || card.accountIdentifier
          )
        ) {
          notifyPastDueLocked();
          return;
        }
      }

      const templateDataToPass: TemplateData = {
        id: assessment.assessment_id,
        name: normalizeTeamAssessmentDisplayName(
          assessment.assessment_name,
          assessment.assessment_name || "Untitled Assessment"
        ),
        json_schema: assessment.questions,
        status: isPublishedView
          ? "published"
          : (assessment.status || "").toLowerCase(),
      };

      setAssessmentResponses(prev => {
        if (executiveAssignment?.response) {
          return {
            ...prev,
            [assessment.assessment_id]: executiveAssignment.response as Record<
              string,
              string | string[]
            >,
          };
        }
        const updated = { ...prev };
        delete updated[assessment.assessment_id];
        return updated;
      });

      setTemplateData(templateDataToPass);
      setSelectedAccountName(account.account_name);
      setSelectedAccountId(account.account_id);
      setCurrentQuestionIndex(0);
      setPreviewModalOpen(true);
    },
    [
      reviewAssessments,
      executiveId,
      activeTab,
      assessmentKindTab,
      selfAssessment,
      notifyPastDueLocked,
    ]
  );

  const voiceModeProps = useMemo(
    () => ({
      enabled: voiceModeEnabled && speechSupported,
      supported: speechSupported,
      isListening,
      onToggle: handleToggleVoiceMode,
      showInstructions: voiceModeEnabled && speechSupported,
    }),
    [voiceModeEnabled, speechSupported, isListening, handleToggleVoiceMode]
  );

  const groupedDisconnectResolutions = useMemo(() => {
    if (grouping === "none") return [];

    const groups = new Map<
      string,
      {
        key: string;
        label: string;
        items: DisconnectResolution[];
        sortValue?: number;
      }
    >();

    filteredDisconnectResolutions.forEach(resolution => {
      let groupKey = "ungrouped";
      let groupLabel = "Ungrouped";
      let sortValue: number | undefined;

      if (grouping === "assessment") {
        groupLabel = resolution.assessmentName || "Unknown Assessment";
        const normalizedName =
          groupLabel.trim().toLowerCase() ||
          resolution.assessment_id ||
          "unknown-assessment";
        groupKey = `assessment-${normalizedName}`;
        sortValue = normalizedName.charCodeAt(0);
      } else if (grouping === "account") {
        groupKey = resolution.account_id || resolution.accountName || "unknown";
        groupLabel = resolution.accountName || "Unknown Account";
        sortValue = groupLabel.toLowerCase().charCodeAt(0);
      } else if (grouping === "due-date") {
        const dueLabel = resolution.due_date
          ? formatAssessmentDateTimePartsUtc(resolution.due_date, "DMY_SHORT")
              .date
          : "No Due Date";
        groupKey = groupLabel = dueLabel;
        sortValue = resolution.due_date
          ? new Date(resolution.due_date).getTime()
          : 0;
      }

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          key: groupKey,
          label: groupLabel,
          items: [],
          sortValue,
        });
      }

      groups.get(groupKey)!.items.push(resolution);
    });

    return Array.from(groups.values()).sort((a, b) =>
      grouping === "due-date"
        ? (a.sortValue || 0) - (b.sortValue || 0)
        : a.label.localeCompare(b.label)
    );
  }, [filteredDisconnectResolutions, grouping]);

  const groupedAssessmentCards = useMemo(() => {
    if (grouping === "none") return [];

    const groups = new Map<
      string,
      { key: string; label: string; cards: any[]; sortValue?: number }
    >();

    // Group by Reassess = filter: only Completed cards that still allow Edit.
    const cardsForGrouping =
      grouping === "reassess"
        ? assessmentCards.filter((card: any) => Boolean(card.showEditButton))
        : assessmentCards;

    cardsForGrouping.forEach((card: any) => {
      let groupKey = "Ungrouped";
      let groupLabel = "Ungrouped";
      let sortValue: number | undefined;

      if (grouping === "assessment") {
        groupLabel = card.assessmentName || "Unknown Assessment";
        const normalizedName =
          groupLabel.trim().toLowerCase() ||
          card.assessmentId ||
          "unknown-assessment";
        groupKey = `assessment-${normalizedName}`;
        sortValue = normalizedName.charCodeAt(0);
      } else if (grouping === "account") {
        groupKey = card.accountIdentifier || card.accountName;
        groupLabel = card.accountName || "Unknown Account";
        sortValue = card.accountName?.toLowerCase().charCodeAt(0);
      } else if (grouping === "due-date") {
        groupKey = groupLabel = card.dueDateLabel || "No Due Date";
        sortValue = card.dueDateValue;
      } else if (grouping === "reassess") {
        groupKey = "reassess";
        groupLabel = "Reassess";
        sortValue = 0;
      }

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          key: groupKey,
          label: groupLabel,
          cards: [],
          sortValue,
        });
      }

      groups.get(groupKey)!.cards.push(card);
    });

    return Array.from(groups.values()).sort((a, b) =>
      grouping === "due-date"
        ? (a.sortValue || 0) - (b.sortValue || 0)
        : a.label.localeCompare(b.label)
    );
  }, [assessmentCards, grouping]);

  return (
    <div className="min-h-full flex flex-col bg-white mt-3">
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            My Assessments
            <AssessmentCalendarStatusBadge />
          </span>
        }
        icon={ClipboardCheck}
        subTitle="Complete assessments for your portfolio accounts"
      />
      <div className="w-full bg-white p-3 md:p-4">
        <div className="rounded-md bg-gray-100 p-4 md:p-4 flex flex-col gap-6">
          <div className="bg-white rounded-lg p-4 border border-gray-200 flex flex-col gap-6 overflow-visible">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between w-full min-w-0">
              <div className="w-full min-w-0 overflow-x-auto overflow-y-visible [-webkit-overflow-scrolling:touch]">
                <TabsComponent
                  tabs={tabs}
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                  variant="tab-segmented"
                />
              </div>

              {activeTab !== "level-matrix" && activeTab !== "status" && (
                <div className="flex items-center gap-2 shrink-0 w-full xl:w-auto justify-end">
                  <div className="w-full min-w-[10rem] max-w-[14rem] sm:max-w-[16rem] sm:w-52">
                    <SearchInput
                      placeholder={
                        activeTab === "disconnect-resolution"
                          ? "Search accounts..."
                          : "Search assessments..."
                      }
                      value={searchQuery}
                      onChange={event => setSearchQuery(event.target.value)}
                    />
                  </div>
                  <DropdownSwitcher
                    className="shrink-0"
                    value={grouping}
                    prefix="Grouping: "
                    options={groupingOptions}
                    onChange={value =>
                      setGrouping((value as AssessmentGrouping) ?? "none")
                    }
                    listboxLabel="Grouping"
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            {activeTab === "level-matrix" ? (
              <LevelMixContainer
                showPortfolioFilter={true}
                allowedScopes={["formal", "team", "my"]}
                defaultScope={urlMixScope}
                initialMixScope={mixScopeParam}
                highlightAccountId={highlightAccountParam}
              />
            ) : activeTab === "status" ? (
              portfolioAccountsLoading || orgPortfolioLoading ? (
                <div className="border border-dashed border-gray-300 rounded-[18px] py-16 px-16 text-center text-gray-500 text-base bg-white shadow-[inset_0_0_0_1px_rgba(148,163,184,0.1)]">
                  Loading assessments…
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <PortfolioAccountsStatusPanel
                    accounts={portfolioAccounts}
                    orgAccounts={orgPortfolioAccounts}
                    error={portfolioAccountsError}
                    onAssess={handleAssessPortfolioAccount}
                    onViewFullReport={setStatusReportAccount}
                    allowInProgressReport={allowInProgressReport}
                    showViewTabs
                  />
                  <StatusAccountFullReport
                    account={statusReportAccount}
                    onClose={() => setStatusReportAccount(null)}
                  />
                </div>
              )
            ) : activeTab === "disconnect-resolution" ? (
              <>
                <div className="mb-4 bg-white rounded-lg border border-gray-200 px-4 pt-2 flex items-end justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1 overflow-x-auto">
                    <TabsComponent
                      tabs={disconnectKindTabs}
                      activeTab={disconnectKindTab}
                      onTabChange={handleDisconnectKindTabChange}
                      variant="tab-underline"
                    />
                  </div>
                  <div className="pb-2 shrink-0">
                    <TabsComponent
                      tabs={disconnectStatusTabs}
                      activeTab={disconnectStatusTab}
                      onTabChange={handleDisconnectStatusTabChange}
                      variant="secondary-segmented"
                      segmentedLayout="fit"
                      className="rounded-full"
                    />
                  </div>
                </div>
                {isLoadingDisconnectResolutions ? (
                  <div className="border border-dashed border-gray-300 rounded-[18px] py-16 px-16 text-center text-gray-500 text-base bg-white shadow-[inset_0_0_0_1px_rgba(148,163,184,0.1)]">
                    Loading disconnect resolutions…
                  </div>
                ) : disconnectResolutionsError ? (
                  <div className="border border-dashed border-red-200 rounded-2xl py-20 px-16 text-center bg-white">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100 mb-4">
                      <Ban className="w-6 h-6 text-red-600" />
                    </div>
                    <p className="text-gray-900 text-base font-semibold mb-1">
                      Unable to load disconnect resolutions
                    </p>
                    <p className="text-gray-600 text-sm">
                      {disconnectResolutionsError}
                    </p>
                  </div>
                ) : filteredDisconnectResolutions.length === 0 ? (
                  <div className="border border-dashed border-gray-300 rounded-2xl py-20 px-16 text-center bg-white">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 mb-4">
                      <AlertTriangle className="w-6 h-6 text-gray-500" />
                    </div>
                    <p className="text-gray-900 text-base font-semibold mb-1">
                      No disconnect resolutions found
                    </p>
                    <p className="text-gray-600 text-sm">
                      {disconnectEmptyMessage}
                    </p>
                  </div>
                ) : grouping !== "none" ? (
                  <div className="flex flex-col gap-5">
                    {groupedDisconnectResolutions.map(group => (
                      <div
                        className="flex flex-col gap-4 bg-white border border-gray-200 rounded-2xl p-4"
                        key={group.key}
                      >
                        <div className="flex items-center justify-between py-2.5 px-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <h3 className="m-0 text-[15px] font-semibold text-gray-900 capitalize">
                            {group.label}
                          </h3>
                          <span className="text-gray-600 text-xs font-medium bg-gray-100 py-1 px-2.5 rounded-md border border-gray-300">
                            {group.items.length}{" "}
                            {group.items.length === 1 ? "item" : "items"}
                          </span>
                        </div>

                        <div className="grid grid-cols-[repeat(auto-fill,minmax(450px,1fr))] gap-4">
                          {group.items.map(resolution => (
                            <DisconnectResolutionCard
                              key={resolution.id}
                              resolution={resolution}
                              canRecordResolution={canRecordResolution(
                                resolution
                              )}
                              canEditResolution={canEditResolution(resolution)}
                              onViewDetails={handleOpenDetailModal}
                              onRecordResolution={handleOpenRecordModal}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(450px,1fr))] gap-4">
                    {filteredDisconnectResolutions.map(resolution => (
                      <DisconnectResolutionCard
                        key={resolution.id}
                        resolution={resolution}
                        canRecordResolution={canRecordResolution(resolution)}
                        canEditResolution={canEditResolution(resolution)}
                        onViewDetails={handleOpenDetailModal}
                        onRecordResolution={handleOpenRecordModal}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {(activeTab === "completed" ||
                  activeTab === "draft" ||
                  activeTab === "assigned") && (
                  <div className="mb-4 bg-white rounded-lg border border-gray-200 px-4 pt-2">
                    <TabsComponent
                      tabs={assessmentKindTabs}
                      activeTab={assessmentKindTab}
                      onTabChange={handleAssessmentKindTabChange}
                      variant="tab-underline"
                    />
                  </div>
                )}

                {reviewAssessmentsLoading ? (
                  <div className="border border-dashed border-gray-300 rounded-[18px] py-16 px-16 text-center text-gray-500 text-base bg-white shadow-[inset_0_0_0_1px_rgba(148,163,184,0.1)]">
                    Loading assessments…
                  </div>
                ) : reviewAssessmentsError ? (
                  <div className="border border-dashed border-red-200 rounded-2xl py-20 px-16 text-center bg-white">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100 mb-4">
                      <Ban className="w-6 h-6 text-red-600" />
                    </div>
                    <p className="text-gray-900 text-base font-semibold mb-1">
                      Unable to load assessments
                    </p>
                    <p className="text-gray-600 text-sm">
                      Please try refreshing the page or contact support if the
                      issue persists
                    </p>
                  </div>
                ) : assessmentCards.length === 0 ||
                  (grouping === "reassess" &&
                    groupedAssessmentCards.every(g => g.cards.length === 0)) ? (
                  <div className="border border-dashed border-gray-300 rounded-2xl py-20 px-16 text-center bg-white">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 mb-4">
                      <CheckCircle className="w-6 h-6 text-gray-500" />
                    </div>
                    <p className="text-gray-900 text-base font-semibold mb-1">
                      No assessments found
                    </p>
                    <p className="text-gray-600 text-sm">
                      {searchQuery
                        ? "Try adjusting your search criteria"
                        : grouping === "reassess"
                          ? "No assessments available to reassess in this view"
                          : activeTab === "assigned" &&
                              assessmentKindTab === "team"
                            ? "No team assessments waiting for you. A teammate may start one from Status → Assess."
                            : "No assessments available for this view"}
                    </p>
                  </div>
                ) : grouping !== "none" ? (
                  <div className="flex flex-col gap-5">
                    {groupedAssessmentCards.map(group => (
                      <div
                        className="flex flex-col gap-4 bg-white border border-gray-200 rounded-2xl p-4"
                        key={group.key}
                      >
                        <div className="flex items-center justify-between py-2.5 px-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <h3 className="m-0 text-[15px] font-semibold text-gray-900 capitalize">
                            {group.label}
                          </h3>
                          <span className="text-gray-600 text-xs font-medium bg-gray-100 py-1 px-2.5 rounded-md border border-gray-300">
                            {group.cards.length}{" "}
                            {group.cards.length === 1 ? "item" : "items"}
                          </span>
                        </div>

                        <div className="grid grid-cols-[repeat(auto-fill,minmax(450px,1fr))] gap-4">
                          {group.cards.map((card: any) => (
                            <ReviewAssessmentCard
                              card={card}
                              key={`${group.key}-${card.id}`}
                              onAction={handleOpenPreviewAssessment}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(450px,1fr))] gap-4">
                    {assessmentCards.map((card: any) => (
                      <ReviewAssessmentCard
                        card={card}
                        key={card.id}
                        onAction={handleOpenPreviewAssessment}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <ReviewAssessmentModal
        isOpen={previewModalOpen}
        templateData={templateData}
        onClose={handleCloseReviewModal}
        responses={assessmentResponses}
        onChange={handleReviewResponseChange}
        onSaveDraft={handleSaveDraft}
        onSubmit={handleSubmitAssessment}
        isSubmitting={isSubmittingReviewAssessment}
        voiceMode={voiceModeProps}
        currentQuestionIndex={currentQuestionIndex}
        onQuestionIndexChange={setCurrentQuestionIndex}
        accountName={selectedAccountName}
        forceReadOnly={previewForceReadOnly}
        teamPeerBanner={previewTeamPeerBanner}
        reassessBanner={previewReassessBanner}
      />

      <DisconnectResolutionDetailModal
        isOpen={detailModalOpen}
        resolution={detailResolution}
        onClose={handleCloseDetailModal}
        onRecordResolution={handleOpenRecordModal}
        canRecordResolution={
          detailResolution ? canRecordResolution(detailResolution) : false
        }
        canEditResolution={
          detailResolution ? canEditResolution(detailResolution) : false
        }
      />

      <RecordResolutionModal
        isOpen={recordModalOpen}
        resolution={recordResolution}
        onClose={handleCloseRecordModal}
        onSuccess={handleRecordResolutionSuccess}
      />

      <AssessmentSubmitSuccessModal
        info={
          selfAssessment.submitSuccessInfo ||
          formalSubmitSuccessInfo ||
          disconnectSubmitSuccessInfo
        }
        showBackToStatus={activeTab !== "status"}
        onClose={() => {
          selfAssessment.closeSuccessModal();
          setFormalSubmitSuccessInfo(null);
          setDisconnectSubmitSuccessInfo(null);
        }}
        onBackToStatus={() => {
          selfAssessment.closeSuccessModal();
          setFormalSubmitSuccessInfo(null);
          setDisconnectSubmitSuccessInfo(null);
          handleTabChange("status");
        }}
        onViewMix={info => {
          selfAssessment.closeSuccessModal();
          setFormalSubmitSuccessInfo(null);
          setDisconnectSubmitSuccessInfo(null);
          goToMyMix(info.accountId);
        }}
      />

      <PendingFormalAssessmentsModal
        isOpen={pendingFormalListOpen}
        onClose={handleClosePendingFormalList}
        loading={pendingFormalListLoading}
        error={pendingFormalListError}
        rows={pendingFormalRows}
        onAssess={handlePendingFormalListAssess}
      />

      <PortfolioAssessChoiceModal
        isOpen={selfAssessment.showChoiceModal}
        isLoading={
          selfAssessment.isValidationLoading && selfAssessment.showChoiceModal
        }
        accountName={selfAssessment.accountName}
        latest={selfAssessment.latestSelfData}
        startingKind={
          selfAssessment.takingKind === "formal" ? "formal" : "team"
        }
        onClose={selfAssessment.resetModal}
        onContinue={selfAssessment.continueFromChoice}
      />

      <ReviewAssessmentModal
        isOpen={selfAssessment.showFormModal}
        templateData={selfAssessment.templateData}
        onClose={selfAssessment.resetModal}
        responses={selfAssessment.responses}
        onChange={selfAssessment.handleResponseChange}
        onSaveDraft={handleSelfAssessmentSaveDraft}
        onSubmit={handleSelfAssessmentSubmit}
        isSubmitting={selfAssessment.isSubmitting}
        portfolioSubmitAction={selfAssessment.submitAction}
        voiceMode={selfAssessment.voiceMode}
        currentQuestionIndex={selfAssessment.questionIndex}
        onQuestionIndexChange={selfAssessment.setQuestionIndex}
        accountName={selfAssessment.accountName}
        footerVariant="portfolio"
        portfolioBlockedMessage={selfAssessment.formalAssessmentPendingMessage}
        isPortfolioValidationLoading={selfAssessment.isValidationLoading}
        onTakeFormalAssessment={
          selfAssessment.hasFormalAssessmentPending
            ? () => {
                void (async () => {
                  const opened =
                    await selfAssessment.takeBlockingFormalAssessment();
                  if (opened) return;
                  void handleTakeFormalAssessment();
                })();
              }
            : undefined
        }
        isTakeFormalAssessmentLoading={
          pendingFormalListLoading || selfAssessment.isTakeFormalLoading
        }
        portfolioAssessMode={selfAssessment.assessMode}
        portfolioFormFlow={selfAssessment.portfolioFormFlow}
        carryForwardNote={selfAssessment.carryForwardNote}
        onCarryForwardNoteChange={selfAssessment.setCarryForwardNote}
        onFormBack={selfAssessment.backToChoice}
        portfolioStatusBadge={selfAssessment.portfolioStatusBadge}
        isFormalFoldIn={selfAssessment.isFormalFoldIn}
        isDraftSession={selfAssessment.isDraftSession}
        forceReadOnly={selfAssessment.forceReadOnly}
        teamPeerBanner={
          selfAssessment.takingKind === "team"
            ? selfAssessment.teamPeerBanner
            : null
        }
        reassessBanner={
          selfAssessment.forceReadOnly || selfAssessment.isDraftSession
            ? null
            : selfAssessment.reassessBanner
        }
        remainderBanner={
          selfAssessment.takingKind === "team"
            ? selfAssessment.privateRemainder
            : null
        }
        canGoToMyAssessments={Boolean(selfAssessment.myAssessmentsNavTarget)}
        onGoToMyAssessments={handleSelfAssessmentGoToMyAssessments}
      />
    </div>
  );
};

export default ReviewAssessments;
